import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { DisplayContext, formatOrderId } from '../utils/orderFormatter';
import { CURRENCY } from '../config/currency';

interface OrderItem {
  id: number;
  quantity: number;
  price: number;
  toppings?: Array<{ id: number; name: string }>;
  menuItem?: {
    id: number;
    name: string;
    freeToppingsCount?: number;
    extraToppingPrice?: number;
  };
}

interface Order {
  id: number;
  customOrderId?: string;
  status: string;
  createdAt: string;
  vendor: {
    id: number;
    username: string;
  };
  qrCode?: {
    code: string;
  };
  deliveryType: 'DELIVER_TO_TABLE' | 'COLLECT';
  total?: number;
  items?: OrderItem[];
  isGuestOrder?: boolean;
  guestName?: string;
  guestPhone?: string;
  guestEmail?: string;
  tableName?: string;
  floorName?: string;
  sectionName?: string;
}

const renderTotal = (order: Order | null): string => {
  if (!order) return 'Calculating...';
  
  if (order.total !== undefined && order.total !== null) {
    return `${CURRENCY.symbol}${order.total.toFixed(2)}`;
  }
  
  if (order.items && order.items.length > 0) {
    const calculatedTotal = order.items.reduce((sum: number, item: OrderItem) => {
      const basePrice = item.price * item.quantity;
      const toppingsCount = item.toppings?.length || 0;
      const freeCount = item.menuItem?.freeToppingsCount || 0;
      const extraPrice = item.menuItem?.extraToppingPrice || 0;
      const extraToppings = Math.max(0, toppingsCount - freeCount);
      const toppingCost = extraToppings * extraPrice * item.quantity;
      return sum + basePrice + toppingCost;
    }, 0);
    return `${CURRENCY.symbol}${calculatedTotal.toFixed(2)}`;
  }
  
  return 'Calculating...';
};

const OrderSuccess: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCartAfterOrderPlacement, setPendingOrderId } = useCart();
  const { isAuthenticated, isGuest } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [vendorSettings, setVendorSettings] = useState<{
    allowModification: boolean;
    allowCancellation: boolean;
  } | null>(null);
  
  // Get custom message from navigation state
  const customMessage = location.state?.message;
  const isTableOrder = location.state?.isTableOrder;

  // CRITICAL: Clear cart and pending order ID when order success page loads
  // This prevents the "modifying existing order" message from appearing after successful order placement
  useEffect(() => {
    if (orderId) {
      console.log('Order success page loaded - clearing cart and pending order ID for order:', orderId);
      clearCartAfterOrderPlacement(orderId);
      setPendingOrderId(null);
      
      // Also clear from localStorage to ensure complete cleanup
      localStorage.removeItem('pendingOrderId');
    }
  }, [orderId, clearCartAfterOrderPlacement, setPendingOrderId]);

  useEffect(() => {
    if (orderId) {
      const fetchOrderWithRetry = async (retries = 3, delay = 500) => {
        try {
          // Strategy: Always try public endpoint first
          // This works for anonymous table orders
          // If order requires auth (403), try authenticated endpoint or redirect to login
          console.log('Fetching order via public endpoint:', orderId);
          const res = await api.get<Order>(`/orders/public/${orderId}`);
          setOrder(res.data);

          // Fetch vendor settings if order is loaded successfully
          if (res.data.vendor?.id) {
            try {
              const settingsRes = await api.get(`/vendors/${res.data.vendor.id}/settings/public`);
              setVendorSettings({
                allowModification: settingsRes.data.allowModification,
                allowCancellation: settingsRes.data.allowCancellation
              });
            } catch (settingsErr) {
              console.error('Failed to fetch vendor settings:', settingsErr);
              // Don't fail if only settings fetch failed
            }
          }
          setLoading(false);
        } catch (err: any) {
          // Handle 403 - order requires authentication
          if (err.response?.status === 403 && err.response?.data?.requiresAuth) {
            console.log('Order requires authentication');

            // If user is authenticated, try the authenticated endpoint
            if (isAuthenticated || isGuest) {
              try {
                console.log('User is authenticated, trying authenticated endpoint');
                const authRes = await api.get<Order>(`/orders/${orderId}`);
                setOrder(authRes.data);

                // Fetch vendor settings
                if (authRes.data.vendor?.id) {
                  try {
                    const settingsRes = await api.get(`/vendors/${authRes.data.vendor.id}/settings/public`);
                    setVendorSettings({
                      allowModification: settingsRes.data.allowModification,
                      allowCancellation: settingsRes.data.allowCancellation
                    });
                  } catch (settingsErr) {
                    console.error('Failed to fetch vendor settings:', settingsErr);
                  }
                }
                setLoading(false);
                return;
              } catch (authErr) {
                console.error('Failed to fetch order with authentication:', authErr);
                toast.error('Failed to load order details');
                setLoading(false);
                return;
              }
            }

            // Not authenticated - redirect to login
            console.log('User not authenticated, redirecting to login');
            toast.error('Please log in to view this order');
            navigate('/login', { state: { from: `/order-success/${orderId}` } });
            return;
          }

          // Handle 404 with retry
          if (retries > 0 && err.response?.status === 404) {
            console.log(`Order not found yet, retrying in ${delay}ms... (${retries} retries left)`);
            setTimeout(() => {
              fetchOrderWithRetry(retries - 1, delay);
            }, delay);
          } else {
            console.error('Failed to fetch order:', err);
            // Only show error toast after all retries are exhausted
            if (err.response?.status === 404) {
              toast.error('Order not found');
            } else {
              toast.error('Failed to load order details');
            }
            setLoading(false);
          }
        }
      };

      fetchOrderWithRetry();
    }
  }, [orderId, isAuthenticated, isGuest, navigate]);

  const handleCancelOrder = async () => {
    if (!order) return;
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success('Order has been cancelled');
      navigate('/orders', { state: { newOrder: true } });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to cancel order';
      toast.error(msg);
    }
  };

  const getEstimatedTime = () => {
    return '12–15 mins'; // This could be dynamic based on order type/size
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="animate-pulse space-y-4">
            <div className="text-6xl mb-4">🎉</div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-full w-3/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-1/2 mx-auto"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-1/3"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full w-1/2 mt-2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <p className="text-[color:var(--status-bad-text)]">Order not found</p>
          <button
            onClick={() => navigate('/orders', { state: { newOrder: true } })}
            className="mt-4 px-4 py-2 bg-[color:var(--action)] text-[color:var(--action-text)] rounded-lg hover:bg-[color:var(--action-hover)]"
          >
            View All Orders
          </button>
        </div>
      </div>
    );
  }

  // Show a loading state if we have an orderId but no order yet
  if (orderId && !order && loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md mx-auto px-4 pt-16 pb-4">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4 animate-bounce">🎉</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Order Placed Successfully!
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Loading order details...
            </p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[color:var(--action)]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md mx-auto px-4 pt-16 pb-4">
        {/* Success Animation */}
        <div className="text-center mb-4 sm:mb-6">
          <div className="text-4xl sm:text-5xl mb-2 sm:mb-3 animate-bounce">🎉</div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {customMessage || (
              order?.isGuestOrder && order?.guestName
                ? `Hi ${order.guestName}! 🎉`
                : isTableOrder
                  ? `Items Added to Table Order!`
                  : order
                    ? `Order ${formatOrderId(order.customOrderId || `#${order.id}`, DisplayContext.CUSTOMER)} Placed Successfully!`
                    : `Order Placed Successfully!`
            )}
          </h1>
          {(order?.isGuestOrder && order?.guestName && !customMessage) && (
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-1">
              Order {formatOrderId(order?.customOrderId || `#${order?.id}`, DisplayContext.CUSTOMER)} Placed Successfully!
            </p>
          )}
          {isTableOrder && (
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-1">
              Your items have been added to order {formatOrderId(order?.customOrderId || (order?.id ? `#${order.id}` : `#${orderId}`), DisplayContext.CUSTOMER)}
            </p>
          )}
          <p className="text-gray-600 dark:text-gray-400">
            {order?.createdAt ? formatDistanceToNow(new Date(order.createdAt)) + ' ago' : ''}
          </p>
        </div>

        {/* Order Info Cards */}
        <div className="space-y-4">
          {/* Estimated Time */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <span className="text-xl">⏳</span>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Estimated Time</p>
                <p className="font-semibold text-gray-900 dark:text-white">{getEstimatedTime()}</p>
              </div>
            </div>
          </div>

          {/* Delivery Type */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <span className="text-xl">{order?.tableName ? '🍽️' : '📍'}</span>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {order?.tableName ? 'Table' : 'Delivery'}
                </p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {order?.tableName ? (
                    <>
                      Table {order.tableName}
                      {order.sectionName && <span className="font-normal text-gray-600 dark:text-gray-400"> • {order.sectionName}</span>}
                      {order.floorName && <span className="font-normal text-gray-600 dark:text-gray-400"> • {order.floorName}</span>}
                    </>
                  ) : (
                    order?.deliveryType === 'DELIVER_TO_TABLE' ? 'To your table' : 'Collect at counter'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Vendor Info */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <span className="text-xl">🏪</span>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Restaurant</p>
                <p className="font-semibold text-gray-900 dark:text-white">{order?.vendor?.username}</p>
              </div>
            </div>
          </div>

          {/* Total Amount */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                <span className="text-xl">💳</span>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Amount</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {renderTotal(order)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 sm:mt-6 space-y-3 pb-4 sm:pb-8">
          <button
            onClick={() => {
              if (order && order.id) {
                navigate(`/orders/${order.id}`);
              } else {
                console.error('Cannot view order details: order is null or missing id');
                // If order is not loaded yet, try to navigate using the orderId from params
                if (orderId) {
                  navigate(`/orders/${orderId}`);
                }
              }
            }}
            disabled={!order && !orderId}
            className={`w-full py-3 px-4 font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2 ${
              !order && !orderId 
                ? 'bg-[color:var(--bg-sunken)] cursor-not-allowed text-[color:var(--text-disabled)]'
                : 'bg-[color:var(--action)] hover:bg-[color:var(--action-hover)] text-[color:var(--action-text)]'
            }`}
          >
            <span>View Order Details</span>
          </button>
          
          {/* Cancel Order Button - Only show for pending/accepted orders when vendor allows cancellation */}
          {(order?.status === 'PENDING' || order?.status === 'ACCEPTED') && vendorSettings?.allowCancellation && (
            <button
              onClick={handleCancelOrder}
              className="w-full py-3 px-4 bg-white dark:bg-gray-800 border-2 border-[color:var(--status-bad)] text-[color:var(--status-bad)] font-semibold rounded-xl hover:bg-[color:var(--status-bad-bg)] transition-colors flex items-center justify-center space-x-2"
            >
              <span>Cancel Order</span>
            </button>
          )}
          
          {/* View All Orders - Always available as fallback */}
          <button
            onClick={() => navigate('/orders', { state: { newOrder: true, orderId: order?.id } })}
            className="w-full py-3 px-4 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
          >
            <span>View All Orders</span>
          </button>
        </div>
      </div>
      {/* Toaster provided globally via ToastProvider */}
    </div>
  );
};

export default OrderSuccess;
